import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Switch from '@mui/material/Switch';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { PUT, UPLOAD, GET, DELETE } from '../../../configs/api';
import { api, states, REQUIRED_1099_THRESHOLD } from '../../../utils/constants';
import { updateUser } from '../../../store/slices/usersSlice';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';
import { canManageTechnicians } from '../../../utils/permissions';
import ActivityTab from './tabs/ActivityTab';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

// Zip code formatting function
const formatZipCode = (value) => {
  // Remove all non-numeric characters
  const zipCode = value.replace(/\D/g, '');
  
  // Format as 12345-6789 (5+4 format)
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

// SSN formatting function (xxx-xx-xxxx)
const formatSSN = (value) => {
  if (!value) return '';
  // Remove all non-numeric characters
  const ssn = value.replace(/\D/g, '');
  
  // Format as xxx-xx-xxxx
  if (ssn.length >= 6) {
    const firstPart = ssn.substring(0, 3);
    const secondPart = ssn.substring(3, 5);
    const thirdPart = ssn.substring(5, 9);
    return `${firstPart}-${secondPart}-${thirdPart}`;
  } else if (ssn.length >= 4) {
    const firstPart = ssn.substring(0, 3);
    const secondPart = ssn.substring(3);
    return `${firstPart}-${secondPart}`;
  } else if (ssn.length > 0) {
    return `${ssn}`;
  }
  return ssn;
};

// EIN formatting function (xx-xxxxxxx)
const formatEIN = (value) => {
  if (!value) return '';
  // Remove all non-numeric characters
  const ein = value.replace(/\D/g, '');
  
  // Format as xx-xxxxxxx
  if (ein.length >= 3) {
    const firstPart = ein.substring(0, 2);
    const secondPart = ein.substring(2, 9);
    return `${firstPart}-${secondPart}`;
  } else if (ein.length > 0) {
    return `${ein}`;
  }
  return ein;
};

const emptyTechnician = {
  id: '',
  type: 'technician',
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  role: 'technician',
  address: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  travel_distance: null,
  latitude: null,
  longitude: null,
  ssn: '',
  ein: ''
};

// Helper function to get display name based on type (technician vs vendor)
const getDisplayName = (technician) => {
  if (technician?.type === 'vendor') {
    return technician.company_name || '';
  }
  return `${technician?.first_name || ''} ${technician?.last_name || ''}`.trim();
};

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, contentRef, keepMounted = false, ...other } = props;
  const isActive = value === index;
  const shouldRender = isActive || keepMounted;

  return (
    <div
      role="tabpanel"
      // Avoid `hidden` when keepMounted — display:none zeros offsetHeight and breaks measuring
      hidden={!isActive && !keepMounted}
      aria-hidden={!isActive}
      id={`technician-tabpanel-${index}`}
      aria-labelledby={`technician-tab-${index}`}
      {...other}
    >
      {shouldRender && (
        <Box
          ref={contentRef}
          sx={{
            p: 3,
            ...(!isActive && keepMounted
              ? {
                  position: 'absolute',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  top: 0,
                  left: 0,
                  right: 0,
                }
              : {}),
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `technician-tab-${index}`,
    'aria-controls': `technician-tabpanel-${index}`,
  };
}

export default function TechnicianDetailsDialog({ selectedTechnician, setSelectedTechnician, user }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const showActivityTab = canManageTechnicians(user);
  const activityTabIndex = 4;
  const dispatch = useDispatch();
  const [editTechnician, setEditTechnician] = useState(emptyTechnician);
  const [editMode, setEditMode] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [activityRefreshToken, setActivityRefreshToken] = useState(0);
  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
    travel_distance: '',
    ssn: '',
    ein: ''
  });
  const [showError, setShowError] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [blockedReason, setBlockedReason] = useState('');
  const [blockedReasonError, setBlockedReasonError] = useState('');
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [originalType, setOriginalType] = useState('technician');
  const [typeChangeDialogOpen, setTypeChangeDialogOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  
  // Earnings date filter state
  const getStartOfYear = () => {
    const now = new Date();
    return new Date(now.getFullYear(), 0, 1); // January 1st of current year
  };
  const getEndOfYear = () => {
    const now = new Date();
    return new Date(now.getFullYear(), 11, 31); // December 31st of current year
  };
  
  const [earningsStartDate, setEarningsStartDate] = useState(getStartOfYear());
  const [earningsEndDate, setEarningsEndDate] = useState(getEndOfYear());
  const [filteredEarnings, setFilteredEarnings] = useState(null);
  /** Paid earnings for the current calendar year only — used for 1099 status (ignores earnings filter). */
  const [calendarYearEarnings, setCalendarYearEarnings] = useState(null);
  const [earningsShowAll, setEarningsShowAll] = useState(false);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [earningsError, setEarningsError] = useState('');
  const isInitialMount = useRef(true);
  const personalInfoContentRef = useRef(null);
  const [lockedContentHeight, setLockedContentHeight] = useState(null);
  
  // Document state - stores actual attachment data from API
  const [documents, setDocuments] = useState({
    w9: [],
    insurance: [],
    msa: [],
    others: []
  });

  // Clear form errors
  useEffect(() => {
    setErrors({
      first_name: '',
      last_name: '',
      company_name: '',
      email: '',
      phone: '',
      address: '',
      address_2: '',
      city: '',
      state: '',
      zip: '',
      travel_distance: '',
      ssn: '',
      ein: ''
    });
    setShowError('');
  }, [editTechnician]);

  // Keep form in sync when the selected technician object updates (e.g. after save).
  useEffect(() => {
    setEditTechnician(selectedTechnician ? {
      ...selectedTechnician,
      latitude: selectedTechnician.latitude || null,
      longitude: selectedTechnician.longitude || null,
      // Format SSN/EIN for display if they exist
      ssn: selectedTechnician.ssn ? formatSSN(selectedTechnician.ssn) : '',
      ein: selectedTechnician.ein ? formatEIN(selectedTechnician.ein) : ''
    } : emptyTechnician);
    setOriginalType(selectedTechnician?.type || 'technician');
    if (selectedTechnician?.id) {
      fetchAttachments(selectedTechnician.id);
    } else {
      setDocuments({ w9: [], insurance: [], msa: [], others: [] });
    }
  }, [selectedTechnician]);

  // Reset tab/filters/activity only when switching to a different technician.
  useEffect(() => {
    setTabValue(0);
    setLockedContentHeight(null);
    setEarningsStartDate(getStartOfYear());
    setEarningsEndDate(getEndOfYear());
    setFilteredEarnings(null);
    setCalendarYearEarnings(null);
    setEarningsShowAll(false);
    setEarningsError('');
    isInitialMount.current = true;
    setActivitiesCount(0);
    setActivityRefreshToken(0);
  }, [selectedTechnician?.id]);

  // Lock tab content height to Personal Info (desktop only); mobile uses flex fill
  useLayoutEffect(() => {
    if (isSmallScreen || !selectedTechnician) {
      setLockedContentHeight(null);
      return;
    }

    const el = personalInfoContentRef.current;
    if (!el) return;

    const height = el.offsetHeight;
    if (height > 0) {
      setLockedContentHeight((prev) => (prev === height ? prev : height));
    }
  }, [
    tabValue,
    selectedTechnician,
    editMode,
    isSmallScreen,
    editTechnician?.type,
    editTechnician?.blacklisted,
    editTechnician?.blocked_reason,
    editTechnician?.address_2,
    editTechnician?.is_active,
  ]);

  // Fetch attachments from API
  const fetchAttachments = async (technicianId) => {
    if (!technicianId) return;
    
    setLoadingAttachments(true);
    try {
      const { status, data } = await GET(`${api.getTechnicianAttachments}/${technicianId}/attachments`);
      if (status === 200 && data?.data?.attachments) {
        // Group attachments by category
        const grouped = {
          w9: [],
          insurance: [],
          msa: [],
          others: []
        };
        
        data.data.attachments.forEach(attachment => {
          if (grouped[attachment.category]) {
            grouped[attachment.category].push(attachment);
          }
        });
        
        setDocuments(grouped);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch filtered earnings from API (date range)
  const fetchFilteredEarnings = useCallback(async () => {
    if (!editTechnician?.id || !earningsStartDate || !earningsEndDate) return;
    
    setLoadingEarnings(true);
    setEarningsError('');
    try {
      const startDateStr = earningsStartDate.toISOString().split('T')[0];
      const endDateStr = earningsEndDate.toISOString().split('T')[0];
      
      const { status, data, message } = await GET(
        `${api.getTechnicianEarningsByDateRange}/${editTechnician.id}/earnings-by-date-range`,
        { start_date: startDateStr, end_date: endDateStr }
      );
      
      if (status === 200) {
        setFilteredEarnings({
          total_earnings: data.total_earnings,
          total_jobs: data.total_jobs
        });
      } else {
        setEarningsError(message || 'Failed to fetch earnings data');
      }
    } catch (error) {
      console.error('Error fetching filtered earnings:', error);
      setEarningsError('Failed to fetch earnings data');
    } finally {
      setLoadingEarnings(false);
    }
  }, [editTechnician?.id, earningsStartDate, earningsEndDate]);

  // Fetch all-time earnings (no date filter)
  const fetchAllEarnings = useCallback(async () => {
    if (!editTechnician?.id) return;
    setLoadingEarnings(true);
    setEarningsError('');
    try {
      const { status, data, message } = await GET(
        `${api.getTechnicianEarningsByDateRange}/${editTechnician.id}/earnings-by-date-range`
      );
      if (status === 200) {
        setFilteredEarnings({
          total_earnings: data.total_earnings,
          total_jobs: data.total_jobs
        });
      } else {
        setEarningsError(message || 'Failed to fetch earnings data');
      }
    } catch (error) {
      console.error('Error fetching all earnings:', error);
      setEarningsError('Failed to fetch earnings data');
    } finally {
      setLoadingEarnings(false);
    }
  }, [editTechnician?.id]);

  // Current calendar year paid earnings for 1099 (independent of the earnings filter UI).
  const fetchCalendarYearEarnings = useCallback(async (technicianId) => {
    if (!technicianId) return;
    try {
      const startDateStr = getStartOfYear().toISOString().split('T')[0];
      const endDateStr = getEndOfYear().toISOString().split('T')[0];
      const { status, data } = await GET(
        `${api.getTechnicianEarningsByDateRange}/${technicianId}/earnings-by-date-range`,
        { start_date: startDateStr, end_date: endDateStr }
      );
      if (status === 200) {
        const raw = data?.total_earnings;
        const amount = raw == null || raw === undefined
          ? 0
          : (typeof raw === 'string' ? parseFloat(raw) : Number(raw));
        setCalendarYearEarnings(Number.isFinite(amount) ? amount : 0);
      }
    } catch (error) {
      console.error('Error fetching calendar-year earnings for 1099:', error);
    }
  }, []);

  // Fetch earnings when date filters change (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (earningsShowAll) return;
    if (editTechnician?.id && earningsStartDate && earningsEndDate) {
      fetchFilteredEarnings();
    }
  }, [earningsStartDate, earningsEndDate, fetchFilteredEarnings, editTechnician?.id, earningsShowAll]);

  useEffect(() => {
    if (!selectedTechnician?.id) {
      setCalendarYearEarnings(null);
      return;
    }
    fetchCalendarYearEarnings(selectedTechnician.id);
  }, [selectedTechnician?.id, fetchCalendarYearEarnings]);

  // Helper functions for earnings and jobs
  const getTotalEarnings = () => {
    // Use filtered earnings if available, otherwise use original data
    const earnings = filteredEarnings !== null 
      ? filteredEarnings.total_earnings 
      : editTechnician?.total_earnings;
    if (earnings == null || earnings === undefined) return 0;
    return typeof earnings === 'string' ? parseFloat(earnings) : earnings;
  };

  const getJobsWorked = () => {
    // Use filtered earnings if available, otherwise use original data
    const jobs = filteredEarnings !== null 
      ? filteredEarnings.total_jobs 
      : editTechnician?.total_jobs;
    return jobs || 0;
  };

  const getAvgTicketCost = () => {
    const earnings = getTotalEarnings();
    const jobs = getJobsWorked();
    if (jobs === 0 || jobs == null || isNaN(jobs) || earnings == null || isNaN(earnings)) return 0;
    const avg = earnings / jobs;
    return isNaN(avg) ? 0 : avg;
  };

  /** Paid YTD for the current calendar year — never follows the earnings date filter / Show all. */
  const getCalendarYearEarnings = () => {
    if (calendarYearEarnings != null) return calendarYearEarnings;
    return 0;
  };

  // Reset earnings filters to current year
  const handleClearEarningsFilters = () => {
    setEarningsShowAll(false);
    setEarningsStartDate(getStartOfYear());
    setEarningsEndDate(getEndOfYear());
    setFilteredEarnings(null);
    setEarningsError('');
  };

  // Show all-time earnings (no date filter)
  const handleShowAllEarnings = () => {
    setEarningsShowAll(true);
    fetchAllEarnings();
  };

  const is1099Required = () => {
    return getCalendarYearEarnings() > REQUIRED_1099_THRESHOLD;
  };

  // Handle blacklist toggle
  const handleToggleBlacklisted = async () => {
    const currentBlacklisted = editTechnician?.blacklisted || false;
    
    // If trying to blacklist, show dialog for reason
    if (!currentBlacklisted) {
      setBlockedReason('');
      setBlockedReasonError('');
      setBlacklistDialogOpen(true);
      return;
    }
    
    // If unblacklisting, proceed directly
    await performToggleBlacklisted(false, null);
  };

  // Perform the actual blacklist toggle API call
  const performToggleBlacklisted = async (blacklisted, reason) => {
    if (!editTechnician?.id) return;
    
    setLoadingBlacklist(true);
    setShowError('');
    try {
      const { status, data, message } = await PUT(
        `technicians/${editTechnician.id}/blacklisted`, 
        {}, 
        { 
          blacklisted, 
          blocked_reason: reason 
        }
      );
      
      if (status === 200) {
        setShowSuccessToast(true);
        dispatch(updateUser({ ...data, role: 'technician' }));
        // Update local state
        setEditTechnician(prev => ({
          ...prev,
          blacklisted: data.blacklisted,
          blocked_reason: data.blocked_reason
        }));
        setBlacklistDialogOpen(false);
        setBlockedReason('');
        setBlockedReasonError('');
        setActivityRefreshToken((token) => token + 1);
      } else {
        setShowError(message || 'Failed to update blacklist status');
      }
    } catch (error) {
      console.error('Failed to toggle blacklisted status:', error);
      setShowError(error.response?.data?.message || 'Failed to update blacklist status');
    } finally {
      setLoadingBlacklist(false);
    }
  };

  // Handle blacklist dialog confirm
  const handleBlacklistDialogConfirm = () => {
    // Validate blocked reason
    if (!blockedReason || blockedReason.trim().length < 10) {
      setBlockedReasonError('Blocked reason is required and must be at least 10 characters');
      return;
    }

    performToggleBlacklisted(true, blockedReason.trim());
  };

  // Handle blacklist dialog cancel
  const handleBlacklistDialogCancel = () => {
    setBlacklistDialogOpen(false);
    setBlockedReason('');
    setBlockedReasonError('');
  };

  const handleToggleActive = async () => {
    if (!editTechnician?.id) return;

    const nextActive = editTechnician?.is_active === false;
    setLoadingActive(true);
    setShowError('');
    try {
      const { status, data, message } = await PUT(
        `technicians/${editTechnician.id}/active`,
        {},
        { is_active: nextActive },
      );

      if (status === 200) {
        setShowSuccessToast(true);
        dispatch(updateUser({ ...data, role: 'technician' }));
        setEditTechnician((prev) => ({
          ...prev,
          is_active: data.is_active,
        }));
        setActivityRefreshToken((token) => token + 1);
      } else {
        setShowError(message || 'Failed to update active status');
      }
    } catch (error) {
      console.error('Failed to toggle active status:', error);
      setShowError(error.response?.data?.message || 'Failed to update active status');
    } finally {
      setLoadingActive(false);
    }
  };

  // Document upload handler
  // Same pattern as blacklist: update Redux + editTechnician only.
  // Do not call setSelectedTechnician here — that object is a useEffect dep and
  // would rebuild the whole form (wiping in-progress profile edits).
  const applyW9Status = (w9) => {
    if (typeof w9 !== 'boolean' || !selectedTechnician?.id) return;
    dispatch(updateUser({ id: selectedTechnician.id, w9, role: 'technician' }));
    setEditTechnician((prev) => ({ ...prev, w9 }));
  };

  const handleFileUpload = async (category, files) => {
    if (!selectedTechnician?.id) {
      setShowError('No technician selected');
      return;
    }

    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setUploading(true);
    setShowError('');

    try {
      // Upload each file
      for (const file of fileArray) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);

        const { status, data, message } = await UPLOAD(
          `${api.uploadTechnicianAttachment}/${selectedTechnician.id}/attachments`,
          formData
        );

        if (status !== 200 && status !== 201) {
          throw new Error(message || 'Failed to upload file');
        }
        // Apply after each success so a later file failure still reflects DB w9.
        if (typeof data?.data?.w9 === 'boolean') {
          applyW9Status(data.data.w9);
        }
      }

      // Refresh attachments list
      await fetchAttachments(selectedTechnician.id);
      setShowSuccessToast(true);
      setActivityRefreshToken((token) => token + 1);
    } catch (error) {
      console.error('Upload error:', error);
      setShowError(error.message || 'Failed to upload file(s)');
    } finally {
      setUploading(false);
    }
  };

  // Document delete handler
  const handleFileDelete = async (category, attachmentId) => {
    if (!selectedTechnician?.id || !attachmentId) {
      setShowError('Invalid attachment');
      return;
    }

    setDeleting(prev => ({ ...prev, [attachmentId]: true }));
    setShowError('');

    try {
      const { status, data, message } = await DELETE(
        `${api.deleteTechnicianAttachment}/${selectedTechnician.id}/attachments/${attachmentId}`
      );

      if (status !== 200) {
        throw new Error(message || 'Failed to delete file');
      }

      if (category === 'w9' && typeof data?.data?.w9 === 'boolean') {
        applyW9Status(data.data.w9);
      }

      // Refresh attachments list
      await fetchAttachments(selectedTechnician.id);
      setShowSuccessToast(true);
      setActivityRefreshToken((token) => token + 1);
    } catch (error) {
      console.error('Delete error:', error);
      setShowError(error.message || 'Failed to delete file');
    } finally {
      setDeleting(prev => {
        const newState = { ...prev };
        delete newState[attachmentId];
        return newState;
      });
    }
  };

  // Get presigned URL for attachment
  const getPresignedUrl = async (attachmentId, attachmentName) => {
    try {
      const { data, status } = await GET(
        `${api.getTechnicianAttachments}/${selectedTechnician.id}/attachments/${attachmentId}/download`
      );
      
      if (status === 200) {
        return data.data.downloadUrl;
      }
      setShowError(`Failed to load ${attachmentName}`);
      return null;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      setShowError(`Failed to load ${attachmentName}: ${error.message || 'Unknown error'}`);
      return null;
    }
  };

  // Document preview handler
  const handleFilePreview = async (attachment) => {
    if (!attachment?.id) {
      setShowError('Invalid attachment');
      return;
    }
    
    const presignedUrl = await getPresignedUrl(attachment.id, attachment.file_name);
    if (presignedUrl) {
      window.open(presignedUrl, '_blank');
    }
  };

  // Document download handler
  const handleFileDownload = async (attachment) => {
    if (!attachment?.id) {
      setShowError('Invalid attachment');
      return;
    }

    const presignedUrl = await getPresignedUrl(attachment.id, attachment.file_name);
    if (presignedUrl) {
      try {
        const response = await fetch(presignedUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.file_name || 'download';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Download error:', error);
        setShowError('Failed to download file');
      }
    }
  };

  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelect = (addressData) => {
    // Format zip code if provided, otherwise clear it
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim()) 
      ? formatZipCode(addressData.zipCode) 
      : '';
    
    setEditTechnician({
      ...editTechnician,
      address: addressData.address || editTechnician.address,
      // Clear city if not provided, otherwise use the provided value
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      // Clear state if not provided, otherwise use the provided value
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip: formattedZip,
      // Store coordinates if available
      latitude: addressData.latitude !== null && addressData.latitude !== undefined ? addressData.latitude : null,
      longitude: addressData.longitude !== null && addressData.longitude !== undefined ? addressData.longitude : null,
    });
  };

  if (!selectedTechnician) return null;

  const buildTechnicianUpdatePayload = () => {
    const isVendor = editTechnician?.type === 'vendor';
    return {
      ...editTechnician,
      type: editTechnician.type || 'technician',
      first_name: isVendor ? '' : editTechnician.first_name,
      last_name: isVendor ? '' : editTechnician.last_name,
      company_name: isVendor ? editTechnician.company_name : '',
      travel_distance: editTechnician.travel_distance === '' || editTechnician.travel_distance === null
        ? null
        : parseInt(editTechnician.travel_distance, 10),
      latitude: editTechnician.latitude,
      longitude: editTechnician.longitude,
      ssn: isVendor ? null : editTechnician.ssn ? editTechnician.ssn.replace(/\D/g, '') : null,
      ein: isVendor ? (editTechnician.ein ? editTechnician.ein.replace(/\D/g, '') : null) : null,
    };
  };

  const performUpdate = async (technicianData) => {
    const isVendor = technicianData.type === 'vendor';
    setLoading(true);
    setShowError('');

    const { status, data, message } = await PUT(api.updateTechnician, {}, technicianData);
    if (status === 200) {
      const formattedRecord = {
        ...data,
        ssn: data.ssn ? formatSSN(data.ssn) : '',
        ein: data.ein ? formatEIN(data.ein) : '',
      };
      setEditMode(false);
      setShowSuccessToast(true);
      setOriginalType(data.type || 'technician');
      setEditTechnician(formattedRecord);
      setSelectedTechnician(formattedRecord);
      dispatch(updateUser({ ...data, role: 'technician' }));
      setActivityRefreshToken((token) => token + 1);
    } else if (status === 409) {
      setShowError(message || `A ${isVendor ? 'vendor' : 'technician'} with this information already exists.`);
    } else {
      setShowError(data?.message || 'Something went wrong, please try again later.');
    }
    setLoading(false);
  };

  const handleUpdateTechnician = async () => {
    const isVendor = editTechnician?.type === 'vendor';
    const typeLabel = isVendor ? 'vendors' : 'technicians';
    
    setShowError('');
    setErrors({
      first_name: '',
      last_name: '',
      company_name: '',
      email: '',
      phone: '',
      address: '',
      address_2: '',
      city: '',
      state: '',
      zip: '',
      travel_distance: '',
      ssn: '',
      ein: ''
    });
    
    let newErrors = {};
    
    // Validation based on type (vendor vs technician)
    if (isVendor) {
      if (!editTechnician.company_name || editTechnician.company_name.length < 2 || editTechnician.company_name.length > 255) {
        newErrors.company_name = 'Company name must be between 2 and 255 characters';
      }
    } else {
      (editTechnician.first_name?.length < 2 || editTechnician.first_name?.length > 50) && (newErrors.first_name = 'First name must be between 2 and 50 characters');
      (editTechnician.last_name?.length < 2 || editTechnician.last_name?.length > 50) && (newErrors.last_name = 'Last name must be between 2 and 50 characters');
    }
    
    if (editTechnician.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editTechnician.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!editTechnician.phone) {
      newErrors.phone = `Phone is required for ${typeLabel}`;
    } else {
      const phoneDigits = editTechnician.phone.replace(/\D/g, '');
      (phoneDigits.length < 10 || phoneDigits.length > 10) && (newErrors.phone = 'Please enter a valid 10-digit US phone number');
    }

    if (editTechnician.address && editTechnician.address.trim().length > 0 && editTechnician.address.trim().length < 2) {
      newErrors.address = 'Address must be at least 2 characters if provided';
    }
    if (editTechnician.address_2 && editTechnician.address_2.length > 255) {
      newErrors.address_2 = 'Address line 2 must not exceed 255 characters';
    }
    if (editTechnician.city && editTechnician.city.trim().length > 0 && editTechnician.city.trim().length < 2) {
      newErrors.city = 'City must be at least 2 characters if provided';
    }
    if (editTechnician.state && editTechnician.state.trim().length > 0 && editTechnician.state.trim().length < 2) {
      newErrors.state = 'State must be at least 2 characters if provided';
    }
    if (editTechnician.zip && editTechnician.zip.trim().length > 0) {
      const zipRegex = /^\d{5}(-\d{4})?$/;
      (!zipRegex.test(editTechnician.zip)) && (newErrors.zip = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)');
    }
    
    if (editTechnician.travel_distance !== null && editTechnician.travel_distance !== '') {
      const travelDistance = typeof editTechnician.travel_distance === 'string' ? parseInt(editTechnician.travel_distance, 10) : editTechnician.travel_distance;
      if (isNaN(travelDistance) || travelDistance < 0 || !Number.isInteger(travelDistance)) {
        newErrors.travel_distance = 'Travel distance must be a non-negative integer';
      }
    }
    
    if (!isVendor && editTechnician.ssn && editTechnician.ssn.trim().length > 0) {
      const ssnDigits = editTechnician.ssn.replace(/\D/g, '');
      if (ssnDigits.length !== 9) {
        newErrors.ssn = 'SSN must be 9 digits (format: xxx-xx-xxxx)';
      }
    } else if (isVendor && editTechnician.ssn && editTechnician.ssn.trim().length > 0) {
      newErrors.ssn = 'SSN should not be provided for vendors';
    }
    
    if (isVendor && editTechnician.ein && editTechnician.ein.trim().length > 0) {
      const einDigits = editTechnician.ein.replace(/\D/g, '');
      if (einDigits.length !== 9) {
        newErrors.ein = 'EIN must be 9 digits (format: xx-xxxxxxx)';
      }
    } else if (!isVendor && editTechnician.ein && editTechnician.ein.trim().length > 0) {
      newErrors.ein = 'EIN should not be provided for technicians';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const technicianData = buildTechnicianUpdatePayload();

    if (technicianData.type !== originalType) {
      setPendingUpdateData(technicianData);
      setTypeChangeDialogOpen(true);
      return;
    }

    await performUpdate(technicianData);
  };

  const handleTypeChangeDialogConfirm = async () => {
    if (!pendingUpdateData) return;
    setTypeChangeDialogOpen(false);
    await performUpdate(pendingUpdateData);
    setPendingUpdateData(null);
  };

  const handleTypeChangeDialogCancel = () => {
    setTypeChangeDialogOpen(false);
    setPendingUpdateData(null);
  };

  // Common TextField sx prop
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#475569' : '#bdbdbd',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#fff' : '#000',
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'text.secondary',
      '&.Mui-focused': {
        color: 'text.primary',
      }
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      color: 'text.primary',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    }
  };

  return (
    <Dialog
      open={!!selectedTechnician}
      onClose={() => {
        setSelectedTechnician(null); 
        setEditMode(false); 
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? '100%' : '60vw',
            maxWidth: isSmallScreen ? '100%' : 'none',
            maxHeight: isSmallScreen ? '100%' : '90vh',
            height: isSmallScreen ? '100%' : undefined,
            m: isSmallScreen ? 0 : undefined,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper',
          cursor: isSmallScreen ? 'default' : 'move',
          borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          fontSize: '1.25rem',
          fontFamily: 'inherit'
        }}>
          {getDisplayName(editTechnician)}
        </Typography>
        <IconButton 
          onClick={() => {
            setSelectedTechnician(null); 
            setEditMode(false); 
          }} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? '#1e293b' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ 
        px: 2,
        pt: 1,
        pb: 1,
        flexShrink: 0,
      }}>
        <Box sx={{
          display: 'inline-flex',
          px: 1.5,
          py: 0.75,
          backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
          borderRadius: '8px 8px 0 0',
          boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="technician details tabs"
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                display: 'none'
              },
              '& .MuiTabs-scrollButtons': {
                color: 'text.secondary'
              },
              '& .MuiTab-root': {
                color: 'text.secondary',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 400,
                minHeight: 'auto',
                py: 0.75,
                px: 2,
                mr: 0.5,
                '&.Mui-selected': {
                  color: 'text.primary',
                  fontWeight: 700,
                  backgroundColor: 'background.paper',
                  borderRadius: '6px',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  borderTop: 'none',
                }
              }
            }}
          >
            <Tab 
              label="Personal Info" 
              {...a11yProps(0)}
              sx={{
                fontWeight: tabValue === 0 ? 700 : 400,
                color: tabValue === 0 ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === 0 ? 'background.paper' : 'transparent',
                border: tabValue === 0 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === 0 ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            <Tab 
              label="Compliance" 
              {...a11yProps(1)}
              sx={{
                fontWeight: tabValue === 1 ? 700 : 400,
                color: tabValue === 1 ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === 1 ? 'background.paper' : 'transparent',
                border: tabValue === 1 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === 1 ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            <Tab 
              label="Documents" 
              {...a11yProps(2)}
              sx={{
                fontWeight: tabValue === 2 ? 700 : 400,
                color: tabValue === 2 ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === 2 ? 'background.paper' : 'transparent',
                border: tabValue === 2 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === 2 ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            <Tab
              label="Earnings & Work Orders"
              {...a11yProps(3)}
              sx={{
                fontWeight: tabValue === 3 ? 700 : 400,
                color: tabValue === 3 ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === 3 ? 'background.paper' : 'transparent',
                border: tabValue === 3 ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: tabValue === 3 ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            {showActivityTab && (
            <Tab
              label={
                <Badge
                  badgeContent={activitiesCount}
                  color="primary"
                  max={999}
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -14,
                      top: 2,
                    },
                  }}
                >
                  Activity
                </Badge>
              }
              {...a11yProps(activityTabIndex)}
              sx={{
                fontWeight: tabValue === activityTabIndex ? 700 : 400,
                color: tabValue === activityTabIndex ? 'text.primary' : 'text.secondary',
                backgroundColor: tabValue === activityTabIndex ? 'background.paper' : 'transparent',
                border: tabValue === activityTabIndex ? (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0') : 'none',
                borderTop: 'none',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                pr: 2.5,
                overflow: 'visible',
                '&:hover': {
                  backgroundColor: tabValue === activityTabIndex ? 'background.paper' : (isDarkMode ? '#1e293b' : '#f0f0f0')
                }
              }}
            />
            )}
          </Tabs>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...(isSmallScreen
            ? { flex: 1 }
            : lockedContentHeight
              ? {
                  height: lockedContentHeight,
                  maxHeight: '100%',
                  flex: '0 1 auto',
                }
              : { flex: '0 1 auto' }),
        }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
        {/* Error Message */}
        {showError && (
          <Box sx={{ p: 3, pb: 0 }}>
            <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
              {showError}
            </Typography>
          </Box>
        )}

        {/* Personal Info Tab */}
        <TabPanel value={tabValue} index={0} contentRef={personalInfoContentRef} keepMounted>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* First Name and Last Name on the same line - OR Company Name for vendors */}
          {editTechnician.type === 'vendor' ? (
            <TextField 
              fullWidth 
              label="Company Name" 
              variant="outlined" 
              slotProps={{ input: { readOnly: !editMode, }, }} 
              value={editTechnician.company_name || ''}
              error={!!errors.company_name}
              helperText={errors.company_name}
              onChange={(event) => setEditTechnician({ ...editTechnician, company_name: event.target.value })}
              sx={textFieldSx}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <TextField 
                sx={{ flex: 1, ...textFieldSx }}
              label="First Name" 
              variant="outlined" 
              slotProps={{ input: { readOnly: !editMode, }, }} 
              value={editTechnician.first_name || ''}
              error={!!errors.first_name}
              helperText={errors.first_name}
              onChange={(event) => setEditTechnician({ ...editTechnician, first_name: event.target.value })}
              />
            <TextField 
                sx={{ flex: 1, ...textFieldSx }}
              label="Last Name" 
              variant="outlined" 
              slotProps={{ input: { readOnly: !editMode, }, }} 
              value={editTechnician.last_name || ''}
              error={!!errors.last_name}
              helperText={errors.last_name}
              onChange={(event) => setEditTechnician({ ...editTechnician, last_name: event.target.value })}
              />
            </Box>
          )}

          {/* Phone and Email on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <TextField 
              sx={{ flex: 1, ...textFieldSx }}
            label="Phone" 
            value={editTechnician.phone || ''} 
            variant="outlined" 
            onChange={(event) => {
              const formattedPhone = formatPhoneNumber(event.target.value);
              setEditTechnician({ ...editTechnician, phone: formattedPhone });
            }}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              readOnly: !editMode,
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                      +1
                    </Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="(555) 123-4567"
            />
            <TextField 
              sx={{ flex: 1, ...textFieldSx }}
              label="Email (Optional)" 
              variant="outlined" 
              type="email"
              slotProps={{ input: { readOnly: !editMode, }, }} 
              value={editTechnician.email || ''}
              error={!!errors.email}
              helperText={errors.email}
              onChange={(event) => setEditTechnician({ ...editTechnician, email: event.target.value })}
            />
          </Box>

          {/* Address */}
          {editMode ? (
            <GooglePlacesAutocomplete
              value={editTechnician.address || ''}
              onChange={(value) => setEditTechnician({ ...editTechnician, address: value })}
              onPlaceSelect={handlePlaceSelect}
              error={!!errors.address}
              helperText={errors.address}
              label="Address (Optional)"
              placeholder="Start typing an address..."
            />
          ) : (
            <TextField 
              fullWidth 
              label="Address (Optional)" 
              variant="outlined" 
              slotProps={{ input: { readOnly: true, }, }} 
              value={editTechnician.address || ''}
              error={!!errors.address}
              helperText={errors.address}
              sx={textFieldSx}
            />
          )}

          {editMode ? (
            <TextField
              fullWidth
              label="Address Line 2 (Optional)"
              value={editTechnician.address_2 || ''}
              onChange={(e) => setEditTechnician({ ...editTechnician, address_2: e.target.value })}
              variant="outlined"
              error={!!errors.address_2}
              helperText={errors.address_2}
              placeholder="Suite, unit, floor, etc."
              sx={textFieldSx}
            />
          ) : editTechnician.address_2 ? (
            <TextField 
              fullWidth 
              label="Address Line 2" 
              variant="outlined" 
              slotProps={{ input: { readOnly: true, }, }} 
              value={editTechnician.address_2 || ''}
              sx={textFieldSx}
            />
          ) : null}

          {/* City, State, and Zip on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              {editMode ? (
                <OptimizedCityAutocomplete
                  value={editTechnician.city || ''}
                  onChange={(value) => setEditTechnician({ ...editTechnician, city: value })}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="City (Optional)" 
                      placeholder="Select or type city name" 
                      fullWidth 
                      error={!!errors.city}
                      helperText={errors.city}
                    />
                  )}
                />
              ) : (
                <TextField 
                  fullWidth 
                  label="City (Optional)" 
                  variant="outlined" 
                  slotProps={{ input: { readOnly: true, }, }} 
                  value={editTechnician.city || ''}
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={textFieldSx}
                />
              )}
            </Box>

          <Autocomplete
            options={Object.keys(states)}
            value={editTechnician.state || ''}
            onChange={(event, value) => setEditTechnician({ ...editTechnician, state: value })}
            getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
            readOnly={!editMode}
            sx={{
                flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiAutocomplete-listbox': {
                borderRadius: 1,
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: 'background.paper',
                padding: 0,
              }
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '8px 16px',
                    margin: 0,
                    backgroundColor: selected ? (isDarkMode ? '#1e293b' : '#f5f5f5') : 'transparent',
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: selected ? 600 : 400,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    {states[option]} - {option}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="State (Optional)" 
                placeholder="Select state" 
                fullWidth 
                error={!!errors.state}
                helperText={errors.state}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'text.primary',
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.75rem',
                  }
                }}
              />
            )}
          />

                <TextField 
              sx={{ flex: 1, ...textFieldSx }}
            label="ZIP Code (Optional)" 
            variant="outlined" 
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editTechnician.zip || ''}
            error={!!errors.zip}
            helperText={errors.zip}
            onChange={(event) => {
              const formattedZip = formatZipCode(event.target.value);
              setEditTechnician({ ...editTechnician, zip: formattedZip });
            }}
            InputProps={{
              readOnly: !editMode,
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="12345 or 12345-6789"
            />
          </Box>

          {/* Type and Travel Distance on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <FormControl sx={{ flex: 1 }} variant="outlined">
              <InputLabel sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: 'text.primary',
                }
              }}>Type</InputLabel>
              <Select
                value={editTechnician.type || 'technician'}
                label="Type"
                onChange={(event) => {
                  const newType = event.target.value;
                  setEditTechnician((prev) => {
                    const next = { ...prev, type: newType };
                    if (newType === 'vendor' && !prev.company_name?.trim()) {
                      const fullName = `${prev.first_name || ''} ${prev.last_name || ''}`.trim();
                      if (fullName) next.company_name = fullName;
                    }
                    if (newType === 'technician' && prev.company_name?.trim()) {
                      if (!prev.first_name?.trim() && !prev.last_name?.trim()) {
                        const parts = prev.company_name.trim().split(/\s+/);
                        next.first_name = parts[0] || '';
                        next.last_name = parts.slice(1).join(' ') || '';
                      }
                    }
                    return next;
                  });
                }}
            sx={{
                borderRadius: 1,
                backgroundColor: 'background.paper',
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                  }
                }}
                disabled={!editMode}
              >
                <MenuItem value="technician" sx={{ fontSize: '0.875rem' }}>Technician</MenuItem>
                <MenuItem value="vendor" sx={{ fontSize: '0.875rem' }}>Vendor</MenuItem>
              </Select>
            </FormControl>

          <TextField 
              sx={{ flex: 1, ...textFieldSx }}
            label="Travel Distance (miles)" 
            variant="outlined" 
            type={editMode ? "number" : "text"}
            slotProps={{ input: { readOnly: !editMode, }, }} 
            value={editMode ? (editTechnician.travel_distance === null ? '' : editTechnician.travel_distance) : (editTechnician.travel_distance ? `${editTechnician.travel_distance} mi` : '')}
            error={!!errors.travel_distance}
            helperText={errors.travel_distance}
            onChange={(event) => {
              const value = event.target.value;
              setEditTechnician({ 
                ...editTechnician, 
                travel_distance: value === '' ? null : value 
              });
            }}
            inputProps={editMode ? { min: 0, step: 1 } : {}}
            placeholder={editMode ? "e.g., 50" : ""}
            />
          </Box>

          {/* SSN - only for technicians */}
          {editTechnician.type !== 'vendor' && (
            <TextField 
              fullWidth 
              label="SSN (Optional)" 
              variant="outlined" 
              slotProps={{ input: { readOnly: !editMode, }, }} 
              value={editTechnician.ssn || ''}
              error={!!errors.ssn}
              helperText={errors.ssn || 'Social Security Number (format: XXX-XX-XXXX)'}
              onChange={(event) => {
                const formattedSSN = formatSSN(event.target.value);
                setEditTechnician({ ...editTechnician, ssn: formattedSSN });
              }}
              placeholder="xxx-xx-xxxx"
              sx={textFieldSx}
            />
          )}

          {/* EIN - only for vendors */}
          {editTechnician.type === 'vendor' && (
            <TextField 
              fullWidth 
              label="EIN (Optional)" 
              variant="outlined" 
              slotProps={{ input: { readOnly: !editMode, }, }} 
              value={editTechnician.ein || ''}
              error={!!errors.ein}
              helperText={errors.ein || 'Employer Identification Number (format: XX-XXXXXXX)'}
              onChange={(event) => {
                const formattedEIN = formatEIN(event.target.value);
                setEditTechnician({ ...editTechnician, ein: formattedEIN });
              }}
              placeholder="xx-xxxxxxx"
              sx={textFieldSx}
            />
          )}

          {/* Active / Archived Toggle - Only for owners and admins */}
          {canManageTechnicians(user) && (
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: editTechnician?.is_active === false ? (isDarkMode ? '#334155' : '#f5f5f5') : (isDarkMode ? '#1e293b' : '#f5f5f5'),
              border: editTechnician?.is_active === false ? '1px solid #9e9e9e' : (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'),
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', mb: 0.5 }}>
                    Active Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {editTechnician?.is_active === false
                      ? 'This technician is archived and cannot be assigned to new jobs'
                      : 'This technician is active and available for job assignments'}
                  </Typography>
                </Box>
                <Switch
                  checked={editTechnician?.is_active !== false}
                  onChange={handleToggleActive}
                  disabled={loadingActive}
                  size="medium"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: isDarkMode ? '#fff' : '#2e7d32',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#2e7d32',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                      backgroundColor: isDarkMode ? '#fff' : '#2e7d32',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: isDarkMode ? '#475569' : '#ccc',
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Blacklisted Toggle - Only for owners and admins */}
          {canManageTechnicians(user) && (
            <Box sx={{ 
              p: 2, 
                  borderRadius: 1,
              backgroundColor: editTechnician?.blacklisted ? (isDarkMode ? '#7f1d1d' : '#ffebee') : (isDarkMode ? '#1e293b' : '#f5f5f5'),
              border: editTechnician?.blacklisted ? '1px solid #d32f2f' : (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0')
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: editTechnician?.blacklisted ? 2 : 0 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', mb: 0.5 }}>
                    Blacklisted Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {editTechnician?.blacklisted ? 'This technician is currently blacklisted' : 'This technician is not blacklisted'}
                  </Typography>
                </Box>
                <Switch
                  checked={editTechnician?.blacklisted || false}
                  onChange={handleToggleBlacklisted}
                  disabled={loadingBlacklist}
                  size="medium"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#d32f2f',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#d32f2f',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                      backgroundColor: '#d32f2f',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: isDarkMode ? '#475569' : '#ccc',
                    },
                  }}
                />
              </Box>
              {editTechnician?.blacklisted && editTechnician?.blocked_reason && (
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: isDarkMode ? '#1e293b' : 'background.paper', 
                  borderRadius: 1, 
                  border: isDarkMode ? '1px solid #991b1b' : '1px solid #ffcdd2'
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 1 }}>
                    Blocked Reason:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.5 }}>
                    {editTechnician.blocked_reason}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Created At and Updated At on the same line */}
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <TextField 
              sx={{ flex: 1, ...textFieldSx }}
            label="Created At" 
            value={editTechnician.created_at ? new Date(editTechnician.created_at).toLocaleDateString() : ''} 
            variant="outlined" 
            slotProps={{ input: { readOnly: true, }, }} 
            />
          <TextField 
              sx={{ flex: 1, ...textFieldSx }}
            label="Updated At" 
            value={editTechnician.updated_at ? new Date(editTechnician.updated_at).toLocaleDateString() : ''} 
            variant="outlined" 
            slotProps={{ input: { readOnly: true, }, }} 
          />
          </Box>
          </Box>
        </TabPanel>

        {/* Compliance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, fontSize: '1.125rem', color: 'text.primary' }}>
              Compliance Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* W-9 Status Section */}
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem', color: 'text.primary' }}>
                  W-9 Status
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: '#fafafa'
                }}>
                  {editTechnician?.w9 ? (
                    <Box sx={{
                      backgroundColor: '#e8f5e9',
                      color: '#2e7d32',
                      px: 2,
                      py: 1.5,
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      W-9 Received
                    </Box>
                  ) : (
                    <>
                      <Box sx={{
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        px: 2,
                        py: 1.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Missing W-9
                      </Box>
                      {/* Request W9 Button - Removed for now */}
                      {/* <Button
                        variant="outlined"
                        sx={{
                          borderColor: '#e0e0e0',
                          color: '#666',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          alignSelf: 'flex-start',
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          '&:hover': {
                            borderColor: '#bdbdbd',
                            backgroundColor: '#fafafa',
                          }
                        }}
                      >
                        Request W-9
                      </Button> */}
                    </>
                  )}
                </Box>
              </Box>

              {/* 1099 Status Section */}
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem', color: 'text.primary' }}>
                  1099 Status
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: '#fafafa'
                }}>
                  {calendarYearEarnings == null ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      Loading 1099 status…
                    </Typography>
                  ) : is1099Required() ? (
                    <>
                      <Box sx={{
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        px: 2,
                        py: 1.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        1099 Required
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        Paid earnings this calendar year: ${getCalendarYearEarnings().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box sx={{
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        px: 2,
                        py: 1.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        1099 Not Required
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        Paid earnings this calendar year: ${getCalendarYearEarnings().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            {/* Left Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* W9 Form Section */}
              <Box sx={{ display: 'flex', gap: 0.375, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: '150px' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary', textDecoration: 'underline' }}>
                    W9 Form
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {documents.w9.length === 0 ? 'No W9 form attached' : `${documents.w9.length} file(s) attached`}
                  </Typography>
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="w9-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload('w9', e.target.files)}
                  />
                  <label htmlFor="w9-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={uploading}
                      sx={{
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        alignSelf: 'flex-start',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                        },
                        '&:disabled': {
                          opacity: 0.6
                        }
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload W9 Form'}
                    </Button>
                  </label>
                  {loadingAttachments ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 1 }}>
                      Loading attachments...
                    </Typography>
                  ) : documents.w9.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {documents.w9.map((attachment) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            backgroundColor: '#e8f5e9',
                            borderRadius: 1,
                          }}
                        >
                          <AttachFileIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {attachment.file_name}
                          </Typography>
                            {attachment.created_at && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {new Date(attachment.created_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                              onClick={() => handleFilePreview(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Preview"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDownload(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Download"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDelete('w9', attachment.id)}
                              disabled={deleting[attachment.id]}
                              sx={{ color: '#c62828', p: 0.5, minWidth: 'auto' }}
                              title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Insurance Section */}
              <Box sx={{ display: 'flex', gap: 0.375, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: '150px' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary', textDecoration: 'underline' }}>
                    Insurance
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {documents.insurance.length === 0 ? 'No Insurance document attached' : `${documents.insurance.length} file(s) attached`}
                  </Typography>
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="insurance-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload('insurance', e.target.files)}
                  />
                  <label htmlFor="insurance-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={uploading}
                      sx={{
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        alignSelf: 'flex-start',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                        },
                        '&:disabled': {
                          opacity: 0.6
                        }
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Insurance'}
                    </Button>
                  </label>
                  {documents.insurance.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {documents.insurance.map((attachment) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            backgroundColor: '#e8f5e9',
                            borderRadius: 1,
                          }}
                        >
                          <AttachFileIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {attachment.file_name}
                          </Typography>
                            {attachment.created_at && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {new Date(attachment.created_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                              onClick={() => handleFilePreview(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Preview"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDownload(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Download"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDelete('insurance', attachment.id)}
                              disabled={deleting[attachment.id]}
                              sx={{ color: '#c62828', p: 0.5, minWidth: 'auto' }}
                              title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right Column */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* MSA Section */}
              <Box sx={{ display: 'flex', gap: 0.375, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: '150px' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary', textDecoration: 'underline' }}>
                    MSA
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {documents.msa.length === 0 ? 'No MSA document attached' : `${documents.msa.length} file(s) attached`}
                  </Typography>
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="msa-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload('msa', e.target.files)}
                  />
                  <label htmlFor="msa-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={uploading}
                      sx={{
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        alignSelf: 'flex-start',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                        },
                        '&:disabled': {
                          opacity: 0.6
                        }
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload MSA'}
                    </Button>
                  </label>
                  {documents.msa.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {documents.msa.map((attachment) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            backgroundColor: '#e8f5e9',
                            borderRadius: 1,
                          }}
                        >
                          <AttachFileIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {attachment.file_name}
                          </Typography>
                            {attachment.created_at && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {new Date(attachment.created_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                              onClick={() => handleFilePreview(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Preview"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDownload(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Download"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDelete('msa', attachment.id)}
                              disabled={deleting[attachment.id]}
                              sx={{ color: '#c62828', p: 0.5, minWidth: 'auto' }}
                              title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Others Section */}
              <Box sx={{ display: 'flex', gap: 0.375, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: '150px' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary', textDecoration: 'underline' }}>
                    Others
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {documents.others.length === 0 ? 'No document attached' : `${documents.others.length} file(s) attached`}
                  </Typography>
                  <input
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="others-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload('others', e.target.files)}
                  />
                  <label htmlFor="others-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={uploading}
                      sx={{
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        alignSelf: 'flex-start',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                        },
                        '&:disabled': {
                          opacity: 0.6
                        }
                      }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Others'}
                    </Button>
                  </label>
                  {documents.others.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {documents.others.map((attachment) => (
                        <Box
                          key={attachment.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            backgroundColor: '#e8f5e9',
                            borderRadius: 1,
                          }}
                        >
                          <AttachFileIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                              {attachment.file_name}
                          </Typography>
                            {attachment.created_at && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {new Date(attachment.created_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <IconButton
                            size="small"
                              onClick={() => handleFilePreview(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Preview"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDownload(attachment)}
                              sx={{ color: 'text.primary', p: 0.5, minWidth: 'auto' }}
                              title="Download"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDelete('others', attachment.id)}
                              disabled={deleting[attachment.id]}
                              sx={{ color: '#c62828', p: 0.5, minWidth: 'auto' }}
                              title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Earnings & Work Orders Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Date range filter: single calendar dropdown (start/end in one picker) */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <DateRangePickerDropdown
                label="Date range"
                placeholder={earningsShowAll ? 'All time' : 'Select date range'}
                startDate={earningsShowAll ? null : earningsStartDate}
                endDate={earningsShowAll ? null : earningsEndDate}
                onStartChange={(d) => { setEarningsShowAll(false); setEarningsStartDate(d); }}
                onEndChange={(d) => { setEarningsShowAll(false); setEarningsEndDate(d); }}
                isDarkMode={isDarkMode}
              />
              <Button
                variant="outlined"
                onClick={handleClearEarningsFilters}
                disabled={loadingEarnings}
                sx={{
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 3,
                  py: 1.25,
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                    backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                  },
                  '&:disabled': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    color: isDarkMode ? '#475569' : '#9e9e9e',
                  }
                }}
              >
                Reset to Current Year
              </Button>
              <Button
                variant={earningsShowAll ? 'contained' : 'outlined'}
                onClick={handleShowAllEarnings}
                disabled={loadingEarnings}
                sx={{
                  ...(earningsShowAll
                    ? {
                        backgroundColor: isDarkMode ? '#fff' : '#000',
                        color: isDarkMode ? '#000' : 'white',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                          boxShadow: 'none',
                        },
                        '&:disabled': {
                          backgroundColor: isDarkMode ? '#475569' : '#666',
                          color: isDarkMode ? '#94a3b8' : '#fff',
                        },
                      }
                    : {
                        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                        },
                        '&:disabled': {
                          borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                          color: isDarkMode ? '#475569' : '#9e9e9e',
                        },
                      }),
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 3,
                  py: 1.25,
                  borderRadius: 1,
                }}
              >
                Show All
              </Button>
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', position: 'relative' }}>
              {loadingEarnings && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(245, 245, 245, 0.7)',
                  borderRadius: 2,
                  zIndex: 1
                }}>
                  <CircularProgress size={40} />
                </Box>
              )}
              {/* Total Earnings */}
              <Box sx={{
                flex: 1,
                minWidth: '200px',
                p: 3,
                backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                borderRadius: 2,
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.875rem' }}>
                  Total Earnings
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.75rem' }}>
                  ${getTotalEarnings().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>

              {/* Work Orders Worked */}
              <Box sx={{
                flex: 1,
                minWidth: '200px',
                p: 3,
                backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                borderRadius: 2,
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.875rem' }}>
                  Total Work Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.75rem' }}>
                  {getJobsWorked()}
                </Typography>
              </Box>

              {/* Avg Ticket Cost */}
              <Box sx={{
                flex: 1,
                minWidth: '200px',
                p: 3,
                backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                borderRadius: 2,
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.875rem' }}>
                  Avg Work Order Cost
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.75rem' }}>
                  ${getAvgTicketCost().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>

            {/* Error Message */}
            {earningsError && (
              <Box sx={{ p: 2, backgroundColor: isDarkMode ? '#7f1d1d' : '#ffebee', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                  {earningsError}
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Activity Tab (owner / GM / admin) */}
        {showActivityTab && (
        <TabPanel value={tabValue} index={activityTabIndex}>
          <ActivityTab
            technicianId={selectedTechnician?.id}
            refreshToken={activityRefreshToken}
            onActivitiesCountChange={setActivitiesCount}
          />
        </TabPanel>
        )}

        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper',
        flexShrink: 0,
      }}>
        {showError && (
          <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600, mr: 'auto' }}>
            {showError}
          </Typography>
        )}
        
        {editMode ? (
          <>
            <Button 
              variant="outlined"
              onClick={() => {
                setEditTechnician(selectedTechnician ? {
                  ...selectedTechnician,
                  ssn: selectedTechnician.ssn ? formatSSN(selectedTechnician.ssn) : '',
                  ein: selectedTechnician.ein ? formatEIN(selectedTechnician.ein) : '',
                } : emptyTechnician);
                setOriginalType(selectedTechnician?.type || 'technician');
                setEditMode(false);
                setTypeChangeDialogOpen(false);
                setPendingUpdateData(null);
              }}
              sx={{ 
                borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                '&:hover': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                }
              }}
            >
              Cancel
            </Button>
            
            <Button 
              disabled={loading} 
              variant="contained" 
              onClick={handleUpdateTechnician}
              sx={{ 
                backgroundColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#000' : 'white',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: isDarkMode ? '#475569' : '#666',
                  color: isDarkMode ? '#94a3b8' : '#fff',
                }
              }}
            >
              Save Changes
            </Button>
          </>
        ) : (
          canManageTechnicians(user) && (
            <Button 
              variant="contained" 
              onClick={() => {
                setOriginalType(selectedTechnician?.type || 'technician');
                setEditMode(true);
              }}
              sx={{ 
                backgroundColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#000' : 'white',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                  boxShadow: 'none',
                }
              }}
            >
              Edit
            </Button>
          )
        )}
      </Box>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Technician updated successfully
        </Alert>
      </Snackbar>

      {/* Type change confirmation dialog */}
      <Dialog
        open={typeChangeDialogOpen}
        onClose={handleTypeChangeDialogCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1, color: 'text.primary' }}>
          Change {originalType === 'vendor' ? 'Vendor' : 'Technician'} Type
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            You are changing this record from {originalType === 'vendor' ? 'Vendor' : 'Technician'} to {editTechnician.type === 'vendor' ? 'Vendor' : 'Technician'}.
          </Typography>
          {/* <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            This will update how this contact appears on all existing jobs, parts, and reports. The {originalType === 'vendor' ? 'company name' : 'first and last name'} will be replaced by the new identity fields.
          </Typography> */}
          {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {editTechnician.type === 'vendor'
              ? 'Any stored SSN will be removed. Use EIN for vendors.'
              : 'Any stored EIN will be removed. Use SSN for technicians.'}
          </Typography> */}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleTypeChangeDialogCancel}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTypeChangeDialogConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
              '&:disabled': {
                backgroundColor: isDarkMode ? '#475569' : '#ccc',
                color: isDarkMode ? '#94a3b8' : '#666',
              }
            }}
          >
            {loading ? 'Saving...' : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blacklist reason dialog */}
      <Dialog
        open={blacklistDialogOpen}
        onClose={handleBlacklistDialogCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1, color: 'text.primary' }}>
          Blacklist Technician
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Please provide a reason for blacklisting {getDisplayName(editTechnician)}. This reason is required and must be at least 10 characters.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Blocked Reason"
            value={blockedReason}
            onChange={(e) => {
              setBlockedReason(e.target.value);
              if (blockedReasonError) {
                setBlockedReasonError('');
              }
            }}
            error={!!blockedReasonError}
            helperText={blockedReasonError || 'Minimum 10 characters required'}
            placeholder="Enter the reason for blacklisting this technician..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleBlacklistDialogCancel}
            sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBlacklistDialogConfirm}
            variant="contained"
            disabled={!blockedReason || blockedReason.trim().length < 10 || loadingBlacklist}
            sx={{ 
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
              '&:disabled': {
                backgroundColor: isDarkMode ? '#475569' : '#ccc',
                color: isDarkMode ? '#94a3b8' : '#666',
              }
            }}
          >
            {loadingBlacklist ? 'Processing...' : 'Confirm Blacklist'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

